<template>
  <div>
    <common-header
      :companyLogo="companyLogo"
      :isSidebarOpen="isSidebarOpen"
      :company="company"
      :permissions="{
        isUsePurchase,
        isUseInventory,
        isUseSmallLot,
        isAdminAndAbove,
        isMasterAdminAndAbove,
      }"
      :serviceID="serviceID"
      :existLocationHistory="existLocationHistory"
      @toggleSidebar="toggleSidebar"
      @saveServiceID="saveServiceID"
    />
    <div class="contents-wrapper">
      <common-sidebar
        :isOpen="isSidebarOpen"
        :permissions="{
          isUsePurchase,
          isUseInventory,
          isUseSmallLot,
          isAdminAndAbove,
          isMasterAdminAndAbove,
          isUseEDI,
        }"
        @saveServiceID="saveServiceID"
      />
      <div class="main-wrapper">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      /** 会社のロゴを表す画像データ */
      companyLogo: '',
      /** サイドバーが展開されているか */
      isSidebarOpen: true,
      /** DBマスタデータ */
      company: {},
      /** サービスID */
      serviceID: '',
      /** ローカルストレージにロケーションの履歴が存在したか */
      existLocationHistory: false,
    }
  },
  async created() {
    // 定期的に認証を確認する。
    setInterval(() => {
      this.$store.dispatch('auth/refresh')
    }, 180 * 1000)
    // ローカルストレージにロケーションの履歴が存在するか確認後、現在のロケーションをローカルストレージに保存する。
    this.existLocationHistory = !!this.loadLocationHistory()
    this.saveLocationHistory()
    // ローカルストレージのサービスIDを取得後、ローカルストレージに保存する。
    this.serviceID = this.loadServiceID()
    this.saveServiceID(this.serviceID)
    // ローカルストレージのサイドバーの開閉状態を取得する。
    this.isSidebarOpen = this.loadIsSidebarOpen()

    // DBマスタデータを取得する。
    try {
      this.company = await this.getCompany()
      this.companyLogo = await this.getClientLogoImage()
    } catch (err) {
      this.$toasted.show(this.$t('データ取得エラーメッセージ'))
    }
  },
  computed: {
    ...mapGetters({
      authorization: 'auth/authorization',
      userdata: 'auth/userdata',
    }),
    /** 受発注管理サービスを表示する権限があるか。 */
    isUsePurchase() {
      return this.company?.isUsePurchase || false
    },
    /** 在庫管理サービスを表示する権限があるか。 */
    isUseInventory() {
      return this.company?.isUseInventory || false
    },
    /** 工程管理サービスを表示する権限があるか。 */
    isUseSmallLot() {
      return this.company?.isUseSmallLot || false
    },
    /** 管理者以上が表示する権限があるものか。 */
    isAdminAndAbove() {
      return this.userdata?.privilege >= 3000 || false
    },
    /** マスター管理者以上が表示する権限があるものか。 */
    isMasterAdminAndAbove() {
      return this.userdata?.privilege >= 5000 || false
    },
    isUseEDI() {
      return this.company?.isUseEDI || false
    },
  },
  methods: {
    /** DBマスタデータを取得する。 */
    async getCompany() {
      const res = await this.$axios.get(
        `${this.$appConfig.CommonApiUrl}/company`,
        {
          headers: {
            Authorization: this.authorization ? this.authorization : null,
          },
        },
      )
      return res.data
    },

    /** S3から会社のロゴを表す画像データを取得する。 */
    async getClientLogoImage() {
      if (this.company?.logoImage) {
        const s3 = await this.$axios({
          method: 'post',
          headers: {
            Authorization: this.authorization ? this.authorization : null,
          },
          url: `${this.$appConfig.CommonApiUrl}/s3/urls`,
          data: {
            targets: [this.company?.logoImage],
          },
        })
        return s3.data[this.company?.logoImage]
      }
    },

    /**
     * サイドバーの表示を切り替える。
     * 又サイドバーの状態をローカルストレージに保持する。
     */
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
      this.saveIsSidebarOpen()
    },

    /** ローカルストレージからサイドバーの開閉状態を取得する。 */
    loadIsSidebarOpen() {
      return (
        localStorage.getItem(this.$appConfig.localStorageKeys.isSidebarOpen) ===
        'true'
      )
    },

    /** サイドバーの開閉状態をローカルストレージに保持する。 */
    saveIsSidebarOpen() {
      localStorage.setItem(
        this.$appConfig.localStorageKeys.isSidebarOpen,
        `${this.isSidebarOpen}`,
      )
    },

    /** ローカルストレージからサービスIDを取得する。 */
    loadServiceID() {
      let serviceID = localStorage.getItem(
        this.$appConfig.localStorageKeys.serviceID,
      )
      if (serviceID == null) {
        if (this.isUsePurchase) {
          serviceID = this.$appConfig.serviceID.purchase
        } else if (this.isUseInventory) {
          serviceID = this.$appConfig.serviceID.inventory
        } else if (this.isUseSmallLot) {
          serviceID = this.$appConfig.serviceID.smallLot
        } else if (this.isMasterAdminAndAbove) {
          serviceID = this.$appConfig.serviceID.base
        } else {
          serviceID = this.$appConfig.serviceID.base
        }
      }
      return serviceID
    },

    /** サービスIDをローカルストレージに保持する。 */
    saveServiceID(serviceID) {
      localStorage.setItem(
        this.$appConfig.localStorageKeys.serviceID,
        serviceID,
      )
    },

    /** ローカルストレージからロケーションの履歴を取得する。 */
    loadLocationHistory() {
      return localStorage.getItem(
        this.$appConfig.localStorageKeys.locationHistory,
      )
    },

    /** 現在のURLのパス名以降をローカルストレージに保持する。 */
    saveLocationHistory() {
      localStorage.setItem(
        this.$appConfig.localStorageKeys.locationHistory,
        location.pathname + location.search + location.hash,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.contents-wrapper {
  // ヘッダーの高さ分移動する。
  margin-top: $HEADER-HEIGHT;

  // ヘッダーの高さ分の高さを減算する。
  height: calc(100vh - #{$HEADER-HEIGHT});
  max-height: calc(100vh - #{$HEADER-HEIGHT});

  // サイドバーとメインコンテンツを並べる。
  display: flex;

  .main-wrapper {
    // メインコンテンツ単体でスクロールさせる。
    width: 100%;
    max-height: calc(100vh - #{$HEADER-HEIGHT});
    overflow: auto;
  }
}
</style>
